import React, { useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import { AlineAvatar } from 'src/components';
import { getOrgOptionUniqIdentifier } from 'src/components/generic';
import type { BoardLocalState } from 'src/components/workflow/board/Board';
import { FilterPopoverContent } from 'src/components/workflow/workflow-list/filter-contents/FilterPopoverContent';
import { Apollo } from 'src/gql/generated';
import { useGetOrgMembers } from 'src/hooks';
import type { ReactSetStateTuple } from 'src/types';

interface BoardAssigneeFilterContentProps {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
}

export function BoardAssigneeFilterContent({
  boardStateTuple,
}: BoardAssigneeFilterContentProps): JSX.Element {
  const [boardState, setBoardState] = boardStateTuple;
  const [searchTerm, setSearchTerm] = useState('');
  const { orgMembers } = useGetOrgMembers({ variables: { filter: Apollo.OrgMemberFilter.Active } });

  const options = useMemo(
    () =>
      orgMembers.map(member => ({
        id: getOrgOptionUniqIdentifier(member),
        label: member.displayName,
        member,
      })),
    [orgMembers]
  );

  const selectedIds = useMemo(
    () => new Set(boardState.filterAssignees.map(a => getOrgOptionUniqIdentifier(a))),
    [boardState.filterAssignees]
  );

  const handleToggle = (optionId: string) => {
    const option = options.find(o => o.id === optionId);
    if (!option) return;

    const isSelected = selectedIds.has(optionId);
    const newAssignees = isSelected
      ? boardState.filterAssignees.filter(a => getOrgOptionUniqIdentifier(a) !== optionId)
      : [...boardState.filterAssignees, option.member];

    setBoardState(s => ({ ...s, filterAssignees: newAssignees }));
  };

  return (
    <FilterPopoverContent
      showSearch
      options={options}
      renderOption={option => (
        <>
          <AlineAvatar avatarInfo={option.member} styles={{ size: 'xs' }} />
          <Typography variant="body2">{option.label}</Typography>
        </>
      )}
      searchPlaceholder="Search assignees..."
      searchTerm={searchTerm}
      selectedIds={selectedIds}
      onSearchChange={setSearchTerm}
      onToggle={handleToggle}
    />
  );
}
